<template>
    <user-layout hasBack backTitle="Patient By Personnel Report" backLink="/reports">

        <template #lowerRight>
            <export-button @click="exporting()" />
        </template>

        <div class="-mx-6 w-[calc(100%+32px)] pb-6" v-if="!loading">
            <div class="mx-6 mt-8 border rounded-lg border-neutral-100">
                <div class="">
                    <data-table 
                        :hasSearch="true"
                        searchPlaceholder="Search Office, Personnel, Patient"
                        :headers="headers" 
                        :no-action="true"
                        :count="items?.data?.length"
                        tableClass="header-frm-table"
                        dataTableClass="border-0"
                        :search="filters.query"
                        @update:searchText="(value:string) => (filters.query = value)"
                    >
                        <template #topContent>
                            <div class="flex space-x-2">
                                <div class="flex px-4 py-3 space-x-2 transition bg-white border rounded cursor-pointer border-neutral-100 hover:bg-primary-100"
                                @click="showFilter = true">
                                    <FunnelIcon class="inline-block w-5 h-5 text-neutral-900" />
                                    <p class="text-sm font-semibold text-neutral-900">Filter</p>
                                </div>
                                
                                <Popover class="relative">
                                    <PopoverButton>
                                        <div class="flex px-4 py-3 space-x-2 transition bg-white border rounded cursor-pointer border-neutral-100 hover:bg-primary-100">
                                            <ArrowsUpDownIcon class="inline-block w-5 h-5 text-neutral-900" />
                                            <p class="text-sm font-semibold text-neutral-900">Sort</p>
                                        </div>
                                    </PopoverButton>

                                    <transition
                                        enter-active-class="transition duration-200 ease-out"
                                        enter-from-class="translate-y-1 opacity-0"
                                        enter-to-class="translate-y-0 opacity-100"
                                        leave-active-class="transition duration-150 ease-in"
                                        leave-from-class="translate-y-0 opacity-100"
                                        leave-to-class="translate-y-1 opacity-0"
                                    >
                                        <PopoverPanel class="absolute right-0 z-10 mt-3 w-fit">
                                        <div class="p-6 overflow-hidden bg-white rounded-lg shadow-md ring-1 ring-black/5 sort">
                                            <div class="flex flex-col w-auto space-y-2 bg-white">
                                                <radio-group
                                                    :options="sortOptions"
                                                    name="sortOptions"
                                                    v-model="filters.column"
                                                    @update:modelValue="getData()"
                                                />
                                            </div>
                                            <hr class="my-4">
                                            <div class="flex flex-col w-auto space-y-2 bg-white">
                                                <radio-group
                                                    :options="orderOptions"
                                                    v-model="filters.order"
                                                    @update:modelValue="getData()"
                                                    name="orderOptions"
                                                />
                                            </div>
                                        </div>
                                        </PopoverPanel>
                                    </transition>
                                </Popover>

                                <FilterPanel
                                    :show="showFilter"
                                    @close="showFilter = false"
                                    @apply:reset="applyReset()"
                                    @apply:filters="applyFilters()"
                                    customClass="!overflow-visible overflow-x-visible"
                                    >
                                    <template #fields>
                                        <div class="space-y-5">
                                            <date-picker
                                                id="date_registered"
                                                name="date_registered"
                                                label="Date Registered"
                                                v-model="filters.date_registered"
                                                range
                                                multiCalendars
                                                class="filter-range"
                                            />
                                            <multi-select
                                                :options="primaryDiagnosisOptions"
                                                id="primary_diagnosis"
                                                name="primary_diagnosis"
                                                label="Primary Diagnosis"
                                                v-model="filters.primary_diagnosis"
                                                placeholder="Select.." />
                                            
                                            <multi-select
                                                :options="personnelOptions"
                                                id="personnel"
                                                name="personnel"
                                                label="Personnel"
                                                v-model="filters.personnel"
                                                searchable
                                                @handle:search="handlePatientSearch"
                                                placeholder="Select.." />
                                        </div>
                                    </template>
                                </FilterPanel>
                            </div>
                        </template>
                        <template #body>
                            <template 
                                v-for="item in items.data" 
                                :key="item"
                            >
                                <tr>
                                    <td class="px-6 py-6 text-sm text-gray-900 whitespace-nowrap">
                                        {{ item?.office }}
                                    </td>
                                    <td class="px-6 py-6 text-sm text-gray-900 whitespace-nowrap">
                                        {{ item?.personnel }}
                                    </td>
                                    <td class="px-6 py-6 text-sm text-gray-900 whitespace-nowrap">
                                        {{ item?.patient }}
                                    </td>
                                    <td class="px-6 py-6 text-sm text-gray-900 whitespace-nowrap">
                                        {{ item?.sex_at_birth }}
                                    </td>
                                    <td class="px-6 py-6 text-sm text-gray-900 whitespace-nowrap">
                                        {{ item?.age }}
                                    </td>
                                    <td class="px-6 py-6 text-sm text-gray-900 whitespace-nowrap">
                                        {{ item?.primary_diagnoses }}
                                    </td>
                                    <td class="px-6 py-6 text-sm text-gray-900 whitespace-nowrap">
                                        {{ item.registered_at ? DateFormatter.customDateFormat(item.registered_at, "MMM dd, yyy - hh:mm a") : null }}
                                    </td>
                                </tr>
                            </template>
                        </template>
                    </data-table>
                    <div class="w-full px-6 border-t py-7">
						<simple-pagination 
							:numRows="filters?.rows ?? 10" 
							:currentPage="items?.current_page" 
							:last-page="items?.last_page"
                            @update="(value: any) => paginate(value.page, value.rows)" 
						/>
					</div>
                </div>
            </div>
        </div>

        <loading-modal
            :show="loading"
            noBtn
            title="Please wait.."
            content="System is currently loading the report based on the selected filter."
        />
    </user-layout>
</template>
<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import UserLayout from "@/layouts/UserLayout.vue";
import ButtonComponent from "@/components/buttons/ButtonComponent.vue";
import DataTable from '@/components/partials/DataTable.vue';
import ExportButton from '@/components/buttons/ExportButton.vue';
import SimplePagination from "@/components/partials/SimplePagination.vue";
import DatePicker from "@/components/inputs/DatePicker.vue";
import RadioGroup from "@/components/inputs/RadioGroup.vue";
import MultiSelect from "@/components/inputs/MultiSelect.vue";
import SelectOptions from "@/components/inputs/SelectOptions.vue";
import FilterPanel from "@/components/partials/FilterPanel.vue";
import LoadingModal from '@/components/modals/LoadingModal.vue';
import { AdjustmentsVerticalIcon, UserGroupIcon, FunnelIcon, ArrowsUpDownIcon } from "@heroicons/vue/24/outline";
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';
import ReportService from '@/classes/Reports/ReportService'; 
import { items } from '@/interfaces/pages/FilterPageInterface'; 

import DateFormatter from '@/helpers/DateFormatter';
import isNil from "lodash/isNil";
import omitBy from "lodash/omitBy";
import debounce from "lodash/debounce";
import http from "@/axios";

const showFilter = ref<boolean>(false);
const loading = ref<boolean>(false);

const sortOptions = [
    { id: '1', label: 'Created', value: 'created_at'}, 
]
const orderOptions = [
    { id: '1', label: 'Ascending', value: 'asc'},
    { id: '2', label: 'Descending', value: 'desc'},
]

const genderOptions = [
    {id: '1', label: 'Male', value: '0'},
    {id: '2', label: 'Female', value: '1'},
    // {id: '3', label: 'LGBTQI+', value: '2'}, // May use in future
]
 
const personnelOptions = ref({});
const primaryDiagnosisOptions = ref({});

const headers: { text: string }[] = [
    { text: 'Office' },
    { text: 'Personnel' },
    { text: 'Patient' },
    { text: 'Sex' },
    { text: 'Age' },
    { text: 'Primary Diagnosis' },
    { text: 'Date Registered' },
];

const filters = ref({
    rows: 10,
    query: null,
    created_at: null,
    primary_diagnosis: null,
    personnel: null, 
    column: 'created_at',
    order: 'asc',
    page: 1,
    date_registered: null,
});


const paginate = ( page:number, rows:number )=>{
    ReportService.paginate(page, rows,filters.value, getData)
}

const applyFilters = () => {
	showFilter.value = false;   
    paginate( 1, filters.value.rows);
};

const applyReset = () => {
    showFilter.value = false;
    filters.value.created_at = null;
    filters.value.personnel = null;
    filters.value.primary_diagnosis = null;
    filters.value.date_registered = null;
    paginate( 1, filters.value.rows);
}; 
const getData =  (): void =>{
    loading.value = true; 
    ReportService.index('patient-diagnoses',omitBy(filters.value, isNil))
        .then(({data})=>{ 
            items.value = data.items; 
            personnelOptions.value = data.props.personnels;
            primaryDiagnosisOptions.value = data.props.primaryDiagnoses; 
        })
        .catch((error: any)=>{
            console.log(error)
        })
        .finally(() => loading.value = false)
} 

const exporting = (): void => {
    loading.value = true;
    ReportService.export('patient-diagnoses',omitBy(filters.value, isNil))
        .then(({data})=>{
             ReportService.downloadExport("patient-diagnoses-export.csv",data);  
             loading.value = false;
        })
        .finally(() => loading.value = false)
}

const handlePatientSearch = async (value: string = null) => {

    if (!value && filters.value.personnel) {
        return;
    }

    const response = await http.get(`/reports/patient-diagnoses/personnels`, { params: {
        search: value
    } });

    if (response.status === 200) {
        personnelOptions.value = response.data;
    }
}


watch(
    () => filters.value.query,
    debounce(() => {
        paginate( 1, filters.value.rows);
        applyReset();
    }, 500)
);

onMounted(()=> {
    getData();
})
</script>